import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import MovingIcon from '@mui/icons-material/Moving';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import 'moment/locale/ko';

import { BoardList, BoardListItem, CmmContainer, DropdownMenu, getColorWithOpacity, SalesTable, SalesTableBody, SalesTableCell, SalesTableHeader, SalesTableRow, TodayTask, TotalSummary, TotalSummaryIconItems } from 'components/Dashboard';
import { LineChart } from 'components/Chart';

const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

export default function DACDashBoard() {
  const navigate = useNavigate();

  const [totalDatas, setTotaldatas] = useState({});
  const [salesRows, setSalesRows] = useState([]);
  const [qnaRows, setQnaRows] = useState([]);
  const [secretRows, setSecretRows] = useState([]);
  const [srchDatas, setSrchDatas] = useState({});
  const [chartParams, setChartParams] = useState({});

  const handleClickAmount = (value) => {
    const { startYmd, endYmd } = value;

    navigate('/dashboard/order-hist', {
      state: {
        startYmd,
        endYmd
      }
    });
  };

  /**
   * 전체현황, 총 매출, 오늘 할일 목록 조회
   */
  const getTotalSummary = async () => {
    const response = await axios.get('/api/dashboard/summary/total');
    if (response) {
      setTotaldatas(response.data);
    }
  };

  /**
   * 매출내역 (연, 분기, 월) 목록 조회
   */
  const getSalesList = async () => {
    const response = await axios.get('/api/dashboard/summary/order', { params: srchDatas });
    if (response) {
      setSalesRows(response.data);
    }
  };

  const getChartRows = async () => {
    const response = await axios.get('/api/dashboard/summary/chart');
    if (response) {
      const params = response.data;
      const categories = params.map((t) => t?.yearMonth);
      const series = [
        {
          name: '주문 건',
          data: params.map((t) => t?.order)
        },
        {
          name: '사용권 구매 수',
          data: params.map((t) => t?.quantity)
        }
      ];
      setChartParams({ categories, series });
    }
  };

  const getQnAList = async () => {
    const response = await axios.get('/api/dashboard/summary/qna');
    if (response) {
      setQnaRows(response.data);
    }
  };

  const getSecretList = async () => {
    const response = await axios.get('/api/dashboard/summary/secret');
    if (response) {
      setSecretRows(response.data);
    }
  };

  const handleChangeQuater = (value) => {
    setSrchDatas({ ...srchDatas, quarter: value });
  };

  const handleChangeMonth = (value) => {
    setSrchDatas({ ...srchDatas, month: value });
  };

  useEffect(() => {
    getTotalSummary();
    getSalesList();
    getChartRows();
    getQnAList();
    getSecretList();
  }, [srchDatas]);

  const today = new Date();
  const quartersBeforeCurrent = quarters.slice(0, Math.floor(today.getMonth() / 3) + 1);
  const monthsBeforeCurrent = months.slice(0, today.getMonth() + 1);

  return (
    <Box sx={{ px: 2, display: 'block' }}>
      {/* <Typography component="h2" variant="h6" color="primary" gutterBottom>
        Dohwa Apps Dashboard
      </Typography> */}
      <Stack spacing={2}>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item xs={9} sm={9} md={9}>
              <TotalSummary title="전체 현황" subtitle="2023년 기준">
                <TotalSummaryIconItems icon={<MovingIcon sx={{ color: 'white' }} fontSize="large" />} title="주문 전체" message={`${totalDatas?.orderCnt} 건`} bgColor={getColorWithOpacity('purple')} />
                <TotalSummaryIconItems icon={<MovingIcon sx={{ color: 'white' }} fontSize="large" />} title="주문 진행중" message={`${totalDatas?.orderEtcCnt} 건`} bgColor={getColorWithOpacity('green')} />
                <TotalSummaryIconItems icon={<MovingIcon sx={{ color: 'white' }} fontSize="large" />} title="주문 완료" message={`${totalDatas?.orderCmptCnt} 건`} bgColor={getColorWithOpacity('yellow')} />
                <TotalSummaryIconItems icon={<MovingIcon sx={{ color: 'white' }} fontSize="large" />} title="신규가입 (업체)" message={`${totalDatas?.companysCnt} 건`} bgColor={getColorWithOpacity('sky')} />
                <TotalSummaryIconItems
                  icon={<MovingIcon sx={{ color: getColorWithOpacity('sky') }} fontSize="large" />}
                  title="신규가입 (개인)"
                  message={`${totalDatas?.registerCnt} 건`}
                  bgColor={getColorWithOpacity('sky', 0.12)}
                  sx={{ mr: 2 }}
                />
              </TotalSummary>
            </Grid>
            <Grid item xs={3} sm={3} md={3}>
              <TotalSummary title="작년 매출" percent={totalDatas?.salesGrowthRate || 0} comment="2022년 대비">
                <Box sx={{ color: 'text.primary', fontSize: 30, fontWeight: 'medium' }}>{totalDatas?.lastYearSales?.toLocaleString('en-us') || 0} 원</Box>
              </TotalSummary>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
            {salesRows.map((row, index) => {
              const color = getColorWithOpacity(row.id, true);
              const { id, label, startYmd, endYmd, totalSales, orderCnt, couponCnt, amount, vat } = row;
              return (
                <Grid key={index} item xs={3} sm={3} md={3}>
                  <SalesTable>
                    <SalesTableHeader
                      title={label}
                      startYmd={startYmd}
                      endYmd={endYmd}
                      salesAmount={totalSales}
                      bgColor={color}
                      onClick={handleClickAmount}
                      component={
                        id === 'quarter' ? (
                          <DropdownMenu defaultValue={quartersBeforeCurrent[quartersBeforeCurrent.length - 1]} options={quartersBeforeCurrent} onChange={handleChangeQuater} />
                        ) : id === 'month' ? (
                          <DropdownMenu defaultValue={monthsBeforeCurrent[monthsBeforeCurrent - 1]} options={monthsBeforeCurrent} onChange={handleChangeMonth} />
                        ) : (
                          <></>
                        )
                      }
                    />
                    <SalesTableBody>
                      <SalesTableRow>
                        <SalesTableCell th sx={{ bgcolor: color, width: '30%' }}>
                          주문건
                        </SalesTableCell>
                        <SalesTableCell unit="건" sx={{ width: '70%', textAlign: 'right' }}>
                          {orderCnt}
                        </SalesTableCell>
                      </SalesTableRow>
                      <SalesTableRow>
                        <SalesTableCell th sx={{ bgcolor: color, width: '30%' }}>
                          사용권수량
                        </SalesTableCell>
                        <SalesTableCell unit="개" sx={{ width: '70%', textAlign: 'right' }}>
                          {couponCnt || 0}
                        </SalesTableCell>
                      </SalesTableRow>
                      <SalesTableRow>
                        <SalesTableCell th sx={{ bgcolor: color, width: '30%' }}>
                          매출액
                        </SalesTableCell>
                        <SalesTableCell unit="원" sx={{ width: '70%', textAlign: 'right' }}>
                          {amount?.toLocaleString() || 0}
                        </SalesTableCell>
                      </SalesTableRow>
                      <SalesTableRow>
                        <SalesTableCell th sx={{ bgcolor: color, width: '30%' }}>
                          세금
                        </SalesTableCell>
                        <SalesTableCell unit="원" sx={{ width: '70%', textAlign: 'right' }}>
                          {vat?.toLocaleString() || 0}
                        </SalesTableCell>
                      </SalesTableRow>
                      <SalesTableRow>
                        <SalesTableCell th sx={{ bgcolor: color, width: '30%' }}>
                          총액
                        </SalesTableCell>
                        <SalesTableCell unit="원" sx={{ width: '70%', textAlign: 'right' }}>
                          {totalSales?.toLocaleString() || 0}
                        </SalesTableCell>
                      </SalesTableRow>
                    </SalesTableBody>
                  </SalesTable>
                </Grid>
              );
            })}
            <Grid item xs={3} sm={3} md={3}>
              <CmmContainer title="오늘의 할일" sx={{ width: '100%', display: 'block' }}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <TodayTask label="신규주문" count={5} />
                    <TodayTask label="세금계산서 발행" count={1} />
                    <TodayTask label="입금완료" count={0} />
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <TodayTask label="QnA" count={0} />
                    <TodayTask label="1:1문의" count={1} />
                  </Stack>
                </Stack>
              </CmmContainer>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item xs={6} sm={6} md={6}>
              <CmmContainer>
                <LineChart
                  title="매출 추이"
                  categories={chartParams?.categories}
                  series={chartParams?.series}
                  tooltip={{
                    y: [
                      {
                        title: {
                          formatter: function (val) {
                            return val + ' (건)';
                          }
                        }
                      },
                      {
                        title: {
                          formatter: function (val) {
                            return val + ' (개)';
                          }
                        }
                      }
                    ]
                  }}
                />
              </CmmContainer>
            </Grid>
            <Grid item xs={3} sm={3} md={3}>
              <CmmContainer title="QnA">
                <BoardList>
                  {qnaRows.map((row) => (
                    <BoardListItem sx={{ mr: 1 }} subject={`${row.subject} (${row.username})`}>
                      <Typography variant="caption" color="textSecondary">
                        작성일: {row.createdAt} | 수정일: {row.updatedAt}
                      </Typography>
                    </BoardListItem>
                  ))}
                </BoardList>
              </CmmContainer>
            </Grid>
            <Grid item xs={3} sm={3} md={3}>
              <CmmContainer title="1:1 문의">
                <BoardList>
                  {secretRows.map((row) => (
                    <BoardListItem sx={{ mr: 1 }} subject={`${row.subject} (${row.username})`}>
                      <Typography variant="caption" color="textSecondary">
                        작성일: {row.createdAt} | 수정일: {row.updatedAt}
                      </Typography>
                    </BoardListItem>
                  ))}
                </BoardList>
              </CmmContainer>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
}
